import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with base configuration
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include user session
authApi.interceptors.request.use(
  (config) => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (isLoggedIn === 'true') {
      // You can add user-specific headers here if needed
      config.headers['X-User-Session'] = 'active';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  company: string;
  phone: string;
  role: 'owner' | 'manager' | 'sales' | 'marketing' | 'admin' | 'other';
}

export interface LoginData {
  email: string;
  password: string;
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  role: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data?: {
    user: User;
  };
  user?: User;
}

export interface ProfileResponse {
  success: boolean;
  data: User;
}

// User Registration
export const registerUser = async (userData: RegisterData): Promise<AuthResponse> => {
  try {
    const response = await authApi.post('/auth/register', userData);
    const data = response.data;
    
    // Handle different response formats
    if (data.success) {
      // Ensure we have the expected structure
      if (!data.data && data.user) {
        // If response has user at root level, restructure it
        return {
          success: true,
          message: data.message || 'Registration successful',
          data: {
            user: data.user
          }
        };
      }
    }
    
    return data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Registration failed');
    }
    throw new Error('Network error during registration');
  }
};

// User Login
export const loginUser = async (credentials: LoginData): Promise<AuthResponse> => {
  try {
    const response = await authApi.post('/auth/login', credentials);
    const data = response.data;
    
    // Handle different response formats
    if (data.success) {
      // Ensure we have the expected structure
      if (!data.data && data.user) {
        // If response has user at root level, restructure it
        return {
          success: true,
          message: data.message || 'Login successful',
          data: {
            user: data.user
          }
        };
      }
    }
    
    return data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Login failed');
    }
    throw new Error('Network error during login');
  }
};

// Get Current User Profile
export const getCurrentUser = async (): Promise<ProfileResponse> => {
  try {
    const response = await authApi.get('/auth/me');
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to get user profile');
    }
    throw new Error('Network error getting user profile');
  }
};

// Update User Profile
export const updateProfile = async (profileData: Partial<User>): Promise<ProfileResponse> => {
  try {
    const response = await authApi.put('/auth/profile', profileData);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to update profile');
    }
    throw new Error('Network error updating profile');
  }
};

// Change Password
export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await authApi.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to change password');
    }
    throw new Error('Network error changing password');
  }
};

// Get All Users (Admin only)
export const getAllUsers = async (): Promise<{ success: boolean; data: User[] }> => {
  try {
    const response = await authApi.get('/auth/users');
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to get users');
    }
    throw new Error('Network error getting users');
  }
};

// Logout
export const logout = (): void => {
  localStorage.removeItem('user');
  localStorage.removeItem('isLoggedIn');
  window.location.href = '/login';
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  const isLoggedIn = localStorage.getItem('isLoggedIn');
  const user = localStorage.getItem('user');
  return isLoggedIn === 'true' && !!user;
};

// Get stored user data
export const getStoredUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

export default {
  registerUser,
  loginUser,
  getCurrentUser,
  updateProfile,
  changePassword,
  getAllUsers,
  logout,
  isAuthenticated,
  getStoredUser,
};
